class AnnouncementSupport extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('.announcement-support__toggle');
    this.panel = this.querySelector('.announcement-support__panel');
    this.overlay = this.querySelector('.announcement-support__overlay');
    this.closeButton = this.querySelector('[data-support-close]');

    this.mql = window.matchMedia('(min-width: 750px)');
    this.hoverDelay = 150;
    this.hoverTimer = null;
    this.isOpen = false;
    this.trapped = false;

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', this.onToggleClick);
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);

    if (this.overlay) this.overlay.addEventListener('click', this.close);
    if (this.closeButton) this.closeButton.addEventListener('click', this.close);

    document.addEventListener('click', this.onDocumentClick);

    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onBreakpointChange);
    } else {
      this.mql.addListener(this.onBreakpointChange);
    }
  }

  disconnectedCallback() {
    if (this.isOpen) this.unlockScroll();

    document.removeEventListener('click', this.onDocumentClick);

    if (this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this.onBreakpointChange);
    } else {
      this.mql.removeListener(this.onBreakpointChange);
    }

    clearTimeout(this.hoverTimer);
  }

  get isDesktop() {
    return this.mql.matches;
  }

  get hoverEnabled() {
    return this.dataset.openOnHover === 'true' && window.matchMedia('(hover: hover)').matches;
  }

  onToggleClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onMouseEnter() {
    if (!this.isDesktop || !this.hoverEnabled) return;

    clearTimeout(this.hoverTimer);
    this.open();
  }

  onMouseLeave() {
    if (!this.isDesktop || !this.hoverEnabled) return;

    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.close(), this.hoverDelay);
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'ESCAPE' || !this.isOpen) return;

    this.close();
    this.toggle.focus();
  }

  onDocumentClick(event) {
    if (!this.isOpen || this.contains(event.target)) return;

    this.close();
  }

  onFocusOut(event) {
    // The mobile drawer is focus trapped, so only the desktop dropdown needs this.
    if (!this.isOpen || !this.isDesktop) return;

    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onBreakpointChange() {
    if (this.isOpen) this.close();
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    // Lifts the announcement bar above the sticky header for both the
    // desktop dropdown and the mobile drawer.
    document.body.classList.add('announcement-support-open');

    // Per-placement hook: the header-icon variant sets data-open-class so its
    // CSS can lift the header (and this panel) above the announcement bar,
    // instead of the announcement bar being lifted above it.
    if (this.dataset.openClass) document.body.classList.add(this.dataset.openClass);

    if (this.isDesktop) return;

    this.panel.setAttribute('aria-modal', 'true');
    this.lockScroll();

    if (typeof trapFocus === 'function') {
      this.trapped = true;
      trapFocus(this.panel, this.closeButton || this.panel);
    }
  }

  close() {
    if (!this.isOpen) return;

    clearTimeout(this.hoverTimer);

    this.isOpen = false;
    this.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('aria-modal', 'false');
    if (this.dataset.openClass) document.body.classList.remove(this.dataset.openClass);
    this.unlockScroll();

    if (this.trapped) {
      this.trapped = false;
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
    }
  }

  lockScroll() {
    if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.body.classList.remove('overflow-hidden', 'announcement-support-open');
    if (this.dataset.openClass) document.body.classList.remove(this.dataset.openClass);
  }
}

if (!customElements.get('announcement-support')) {
  customElements.define('announcement-support', AnnouncementSupport);
}
